import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./GroupLoanNOCLetter.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";

const GroupLoanNOCLetter = () => {
  const glNocPageRef = useRef();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { profile } = useSelector((state) => state.userProfile);
  const [company, setCompany] = useState(null);

  const loan = state || null;

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (!profile?.superAdminId) return;

    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile.superAdminId}`,
        );
        const data = await res.json();

        if (res.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchCompany();
  }, [profile?.superAdminId]);

  if (!loan) {
    return (
      <div className="glnoc-error-view">
        <p>No group loan closure data found</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const downloadPDF = async () => {
    if (!glNocPageRef.current) return;

    try {
      window.scrollTo(0, 0);

      const canvas = await html2canvas(glNocPageRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.8);
      const pdf = new jsPDF("p", "pt", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const margin = 25;
      const printableWidth = pdfWidth - margin * 2;
      const printableHeight = (canvas.height * printableWidth) / canvas.width;

      let heightLeft = printableHeight;
      let position = margin;

      pdf.addImage(
        imgData,
        "JPEG",
        margin,
        position,
        printableWidth,
        Math.min(printableHeight, pdfHeight - margin * 2),
        undefined,
        "FAST",
      );
      heightLeft -= pdfHeight - margin * 2;

      while (heightLeft > 0) {
        position = heightLeft - printableHeight + margin;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          margin,
          position,
          printableWidth,
          Math.min(heightLeft, pdfHeight - margin * 2),
          undefined,
          "FAST",
        );
        heightLeft -= pdfHeight - margin * 2;
      }

      pdf.save(
        `${loan.memberName || loan.member?.fullName || "Member"}_GroupLoan_NOC.pdf`,
      );
    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };

  const printNOC = () => {
    if (!glNocPageRef.current) return;

    const printWindow = window.open("", "_blank", "width=900,height=750");

    printWindow.document.write(`
      <html>
        <head>
          <title>Group Loan NOC Document</title>
          <style>
            @page { size: A4; margin: 0; }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 40px;
              background-color: #ffffff;
              color: #000000;
              -webkit-print-color-adjust: exact;
            }
            .glnoc-document-frame {
              width: 100%;
              border: 2px solid #000000 !important;
              padding: 30px;
              box-sizing: border-box;
              background: #ffffff;
            }
            .glnoc-header-flex {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #000000;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .glnoc-brand-logo-box { width: 85px; height: 85px; }
            .glnoc-brand-logo { width: 100%; height: 100%; object-fit: contain; }
            .glnoc-meta-corporate-info { text-align: center; flex: 1; }
            .glnoc-meta-corporate-info h1 { font-size: 20px; font-weight: bold; margin: 0 0 5px 0; color: #003366; text-transform: uppercase; }
            .glnoc-meta-corporate-info p { font-size: 12.5px; margin: 2px 0; color: #222; }
            .glnoc-main-doc-title { text-align: center; font-size: 19px; font-weight: bold; text-transform: uppercase; margin: 25px 0 15px 0; letter-spacing: 0.5px; }
            .glnoc-dated-row { text-align: right; font-size: 13.5px; margin-bottom: 20px; }
            .glnoc-narrative-paragraph { font-size: 13.5px; line-height: 1.6; margin-bottom: 15px; text-align: justify; color: #111; }
            .glnoc-structured-data-table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 13.5px; }
            .glnoc-structured-data-table td { border: 1px solid #000000 !important; padding: 8px 12px; vertical-align: top; }
            .glnoc-structured-data-table td:first-child { font-weight: bold; width: 38%; background-color: #fafafa; }
            .glnoc-corporate-signatures-area { margin-top: 45px; display: flex; flex-direction: column; font-size: 13.5px; }
            .glnoc-compliance-footnote { margin-top: 40px; border-top: 1px dashed #777777; padding-top: 12px; font-size: 11px; color: #444444; text-align: center; page-break-inside: avoid; }
            .glnoc-balanced-spacer { width: 85px; }
          </style>
        </head>
        <body>
          <div class="glnoc-document-frame">
            ${glNocPageRef.current.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  const renderDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-GB");
  };

  return (
    <div className="glnoc-screen-wrapper">
      <div className="glnoc-actions-toolbar no-print">
        <button
          className="glnoc-action-btn glnoc-btn-download"
          onClick={downloadPDF}
        >
          Download PDF
        </button>
        <button className="glnoc-action-btn glnoc-btn-print" onClick={printNOC}>
          Print NOC
        </button>
      </div>

      <div className="glnoc-document-frame" ref={glNocPageRef}>
        <div className="glnoc-header-flex">
          <div className="glnoc-brand-logo-box">
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="Company Logo"
                className="glnoc-brand-logo"
                crossOrigin="anonymous"
              />
            )}
          </div>

          <div className="glnoc-meta-corporate-info">
            <h1>{(company?.name || "Techvision").toUpperCase()}</h1>
            <p>
              <b>Address:</b> {company?.address || "-"}
            </p>
            <p>
              <b>CIN:</b> {company?.cin || "-"}
            </p>
            <p>
              <b>Mobile:</b> {company?.contact || "-"}
            </p>
            <p>
              <b>Email:</b> {company?.email || "-"}
            </p>
          </div>
        </div>

        <div className="glnoc-body-content">
          <h2 className="glnoc-main-doc-title">
            NO OBJECTION CERTIFICATE (NOC)
          </h2>

          <p className="glnoc-dated-row">
            Date: <strong>{new Date().toLocaleDateString("en-GB")}</strong>
          </p>

          <p className="glnoc-narrative-paragraph">
            This is to certify that{" "}
            <strong>{loan.memberName || loan.member?.fullName || "—"}</strong>,
            residing at <strong>{loan?.member?.address || "—"}</strong>, has
            successfully cleared and satisfied all requirements for the group
            loan account availed from{" "}
            <strong>{company?.name || "our institution"}</strong>.
          </p>

          <table className="glnoc-structured-data-table">
            <tbody>
              <tr>
                <td>Member / Customer Name</td>
                <td>{loan.memberName || loan.member?.fullName || "N/A"}</td>
              </tr>
              <tr>
                <td>Customer ID</td>
                <td>{loan.member?.userId || loan.customerId || "N/A"}</td>
              </tr>
              <tr>
                <td>Center Name</td>
                <td>{loan.centerName || "N/A"}</td>
              </tr>
              <tr>
                <td>Group Name</td>
                <td>{loan.groupName || "N/A"}</td>
              </tr>
              <tr>
                <td>Group Leader Name</td>
                <td>{loan.groupLeaderName || "N/A"}</td>
              </tr>
              <tr>
                <td>Loan Account Number</td>
                <td>{loan.loanAccountNumber || "N/A"}</td>
              </tr>
              <tr>
                <td>Loan Type</td>
                <td>Group Loan {loan?.loanType ? `(${loan.loanType})` : ""}</td>
              </tr>
              <tr>
                <td>Loan Disbursed Amount</td>
                <td>₹ {loan.loanAmount || "0"}</td>
              </tr>
              <tr>
                <td>Loan Closing Date</td>
                <td>
                  {loan?.closedAt
                    ? renderDate(loan.closedAt)
                    : loan?.closureDate
                      ? renderDate(loan.closureDate)
                      : renderDate(new Date())}
                </td>
              </tr>
              {loan?.status?.toUpperCase() === "FORCE_CLOSED" && (
                <>
                  <tr>
                    <td>Force Closed Amount / Settlement Amount</td>
                    <td style={{ color: "#c5221f", fontWeight: "bold" }}>
                      ₹{" "}
                      {loan?.clouseramount ||
                        loan?.settlementAmount ||
                        "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Reason for Force Closure</td>
                    <td>{loan?.closureReason || loan?.reason || "N/A"}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          <p className="glnoc-narrative-paragraph">
            We hereby confirm that the above-mentioned group loan account has
            been{" "}
            {loan?.status?.toUpperCase() === "FORCE_CLOSED" ? (
              <strong style={{ color: "#c5221f" }}>
                FORCE CLOSED & SETTLED
              </strong>
            ) : (
              <strong>fully repaid</strong>
            )}
            , settling all principal components, interests, and additional
            structural parameters. There are{" "}
            <strong>no outstanding dues</strong> outstanding against this
            account as of date.
          </p>

          <p className="glnoc-narrative-paragraph">
            Accordingly, we have <strong>no objection</strong> in officially
            formalizing the closing of the said loan account ledger, discharging
            the group liability obligations contextually mapped onto the
            customer.
          </p>

          <div className="glnoc-corporate-signatures-area">
            <p>
              For <strong>{company?.name || "Techvision"}</strong>
            </p>
            <br />
            <br />
            <p>
              <strong>Authorized Signatory</strong>
            </p>
          </div>
        </div>

        {/* SYSTEM DISCLOSURE NOTICE */}
        {/* <div className="glnoc-compliance-footnote">
          <p>This is a system generated document. Signature with official corporate stamp is required for validation.</p>
        </div> */}
      </div>
    </div>
  );
};

export default GroupLoanNOCLetter;
